import { DossierStatus } from "@/entities/dossier";
import { DossierDetailsDto, TrajectDto, TreinIncidentScenarioDto } from "@/shared/types";
import { capitalizeFirstChar } from "@/shared/utils";
import { parseISO } from "date-fns";
import z from "zod";
import { Reikwijdte } from "../constants/reikwijdte";
import { Prognose } from "./prognose";
import { SpoorwebData } from "./spoorweb";

export type Traject = {
    vanStation: string;
    naarStation: string;
};

export const Traject = {
    fromDto: (dto: TrajectDto) => ({
        naarStation: dto.naarStation,
        vanStation: dto.vanStation,
    }),
    format: (trajecten: Traject[]): string =>
        trajecten && trajecten.length !== 0
            ? trajecten.map((t) => `${capitalizeFirstChar(t.vanStation)} - ${capitalizeFirstChar(t.naarStation)}`).join(", ")
            : "Onbekend",
};

const trajectSchema = z.object({
    vanStation: z.string().trim().nonempty("Kies een beginstation."),
    naarStation: z.string().trim().nonempty("Kies een eindstation."),
});

export const trajectenSchema = z
    .array(trajectSchema)
    .min(1, "Voeg minimaal één traject toe.")
    .refine((arr) => {
        return new Set(arr.map((t) => `${t.vanStation}-${t.naarStation}`.toLowerCase())).size === arr.length;
    }, "Trajecten mogen niet dubbel voorkomen.");

export type DossierDetails = {
    id: number;
    displayNaam: string;
    status: DossierStatus;
    reikwijdte: Reikwijdte;
    stations: string[];
    spoorweb?: SpoorwebData;
    trajecten: Traject[];
    startDatumTijd: Date;
    eindDatumTijd: Date | null;
    omschrijving: string;
    omschrijvingExtraContext: string | null;
    prognose: Prognose | null;
    treindienstLeiderGebied: string[];
    treinIncidentScenario: TreinIncidentScenario | null;
};

export const DossierDetails = {
    fromDto: (dossierId: number, dto: DossierDetailsDto): DossierDetails => ({
        id: dossierId,
        displayNaam: dto.displayNaam,
        status: dto.status,
        reikwijdte: dto.reikwijdte,
        stations: dto.stations,
        spoorweb: SpoorwebData.fromDto(dto.spoorweb),
        trajecten: dto.trajecten,
        startDatumTijd: parseISO(dto.startDatumTijd),
        eindDatumTijd: dto.eindDatumTijd ? parseISO(dto.eindDatumTijd) : null,
        omschrijving: dto.omschrijving,
        omschrijvingExtraContext: dto.omschrijvingExtraContext ?? null,
        prognose: dto.prognose ? Prognose.fromDto(dto.prognose) : null,
        treindienstLeiderGebied: dto?.treindienstLeiderGebied,
        treinIncidentScenario: dto.treinIncidentScenario ? TreinIncidentScenario.fromDto(dto.treinIncidentScenario) : null,
    }),
};

type TreinIncidentScenario = {
    code: string;
    gewijzigdDatumTijd?: Date;
};

const TreinIncidentScenario = {
    fromDto: (dto: TreinIncidentScenarioDto): TreinIncidentScenario => ({
        code: dto.code,
        gewijzigdDatumTijd: parseISO(dto.gewijzigdDatumTijd),
    }),
};
